// "Already typed" detection for the ranker's in-tier tie-breaker.
//
// A value the user has already written in the clause they are completing is
// still a legal suggestion, but it must not LEAD the list: at
// `EXEC proc @A = 1, ▮` the remaining parameters come first, at
// `IN ('G', '▮` the other enum values do, at `PRIMARY KEY (k1, ▮` the
// other declared columns do. The signal is engine-agnostic — it reads only
// the statement text before the caret — so the same policy applies to all
// five dialects.
//
// Region scanned = the text after the LATER of
//   - the innermost unmatched `(` before the caret (the enclosing list), or
//   - the last clause keyword at paren depth 0 (`SELECT`, `WHERE`, `EXEC`, …).
// String-literal contents are collected as list items (enum values) and then
// blanked, so identifiers inside a literal — the `N'@P INT, @Q MONEY'`
// parameter string of `sp_executesql` — never count as typed.

import type { CompletionCandidate, CompletionCandidateKind } from '@/core/engines';

const CLAUSE_KEYWORD_RE =
  /(?:SELECT|FROM|WHERE|SET|VALUES|GROUP\s+BY|ORDER\s+BY|PARTITION\s+BY|HAVING|ON|INTO|EXEC|EXECUTE|CALL|RETURNING|JOIN|USING|UPDATE|DELETE|INSERT|MERGE|WITH|BEGIN)(?![\w$])/iy;

const IDENTIFIER_RE = /[@#:$]?[A-Za-z_][\w$]*/g;

/**
 * Lower-cased names (identifiers, `@locals`, quoted identifiers and string
 * literal contents) already present in the clause / paren list the caret is
 * in, plus whether that region is a comma list (see {@link isAlreadyTyped}). `statementPrefix` is the statement text from its start to the caret.
 * The partial token at the caret (what the user is typing right now) is
 * excluded — it is the prefix being completed, not a prior mention.
 */
export type AlreadyTyped = {
  readonly names: ReadonlySet<string>;
  /**
   * True when the scanned region is a comma-separated list (a top-level `,`
   * precedes the caret). Only there is a repeated non-variable name a
   * near-certain mistake: `a.id = b.▮id` in a join predicate repeats the
   * column name legitimately, `WHEN … THEN … ▮WHEN` repeats a keyword.
   */
  readonly inCommaList: boolean;
};

const EMPTY: AlreadyTyped = { names: new Set(), inCommaList: false };

/** Kinds never demoted for repetition — SQL repeats them by design. */
const REPETITION_EXEMPT_KINDS: ReadonlySet<CompletionCandidateKind> = new Set([
  'keyword',
  'system-function',
  'routine',
]);

/**
 * Whether `candidate` (lower-cased bare name `nameLower`) should be demoted
 * as already typed: variables and parameters anywhere in the clause (a
 * second `@x` in one predicate list is rare), everything else only inside
 * a comma-separated list, keywords/functions never.
 */
export function isAlreadyTyped(
  candidate: CompletionCandidate,
  nameLower: string,
  typed: AlreadyTyped,
): boolean {
  if (!typed.names.has(nameLower)) return false;
  if (candidate.kind === 'variable' || candidate.kind === 'parameter') return true;
  if (REPETITION_EXEMPT_KINDS.has(candidate.kind)) return false;
  return typed.inCommaList;
}

export function collectAlreadyTypedNames(statementPrefix: string): AlreadyTyped {
  const names = new Set<string>();
  if (statementPrefix.length === 0) return EMPTY;

  const withoutComments = statementPrefix
    .replace(/--[^\n]*/g, (m) => ' '.repeat(m.length))
    .replace(/\/\*[\s\S]*?\*\//g, (m) => ' '.repeat(m.length));
  const withoutCaretToken = withoutComments.replace(/[@#:$]?[\w$]*$/, '');

  // Literals: collect closed ones as list items, blank all of them (an
  // unterminated trailing literal is the value being typed — dropped).
  const literalsBlanked = withoutCaretToken.replace(
    /'((?:[^']|'')*)'|'[^']*$/g,
    (m, body: string | undefined) => {
      if (body !== undefined) names.add(body.replace(/''/g, "'").toLowerCase());
      return ' '.repeat(m.length);
    },
  );

  const region = literalsBlanked.slice(regionStart(literalsBlanked));
  for (const m of region.matchAll(/\[([^\]]+)\]|"([^"]+)"|`([^`]+)`/g)) {
    names.add((m[1] ?? m[2] ?? m[3] ?? '').toLowerCase());
  }
  const unquoted = region.replace(/\[[^\]]*\]|"[^"]*"|`[^`]*`/g, (m) => ' '.repeat(m.length));
  for (const m of unquoted.matchAll(IDENTIFIER_RE)) {
    names.add(m[0].toLowerCase());
  }
  return { names, inCommaList: hasTopLevelComma(unquoted) };
}

function hasTopLevelComma(region: string): boolean {
  let depth = 0;
  for (const ch of region) {
    if (ch === '(') depth++;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    else if (ch === ',' && depth === 0) return true;
  }
  return false;
}

/** Offset in `text` where the enclosing list / current clause begins. */
function regionStart(text: string): number {
  let depth = 0;
  let openParen = -1;
  for (let i = text.length - 1; i >= 0; i--) {
    const ch = text[i];
    if (ch === ')') depth++;
    else if (ch === '(') {
      if (depth === 0) {
        openParen = i + 1;
        break;
      }
      depth--;
    }
  }
  const afterParen = openParen === -1 ? 0 : openParen;
  const scope = text.slice(afterParen);
  let clauseStart = 0;
  let d = 0;
  // Clause keywords only count at paren depth 0 relative to the scope start.
  for (let i = 0; i < scope.length; i++) {
    const ch = scope[i];
    if (ch === '(') d++;
    else if (ch === ')') d = Math.max(0, d - 1);
    else if (d === 0 && /[A-Za-z]/.test(ch) && (i === 0 || !/[\w$@#:]/.test(scope[i - 1]!))) {
      CLAUSE_KEYWORD_RE.lastIndex = i;
      const m = CLAUSE_KEYWORD_RE.exec(scope);
      if (m) {
        clauseStart = i + m[0].length;
        i = clauseStart - 1;
      }
    }
  }
  return afterParen + clauseStart;
}
